import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  type User,
} from 'firebase/auth';
import { getFirebaseAuth, googleProvider, firebaseConfigured } from '@/lib/firebase';
import { setTokenProvider } from '@/lib/api';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  configured: boolean;
  isAdmin: boolean;
  signInEmail: (email: string, password: string) => Promise<void>;
  registerEmail: (email: string, password: string, displayName?: string) => Promise<void>;
  signInGoogle: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const auth = getFirebaseAuth();

  useEffect(() => {
    // Feed the API client a fresh token on demand.
    setTokenProvider(async () => {
      const current = getFirebaseAuth()?.currentUser;
      return current ? current.getIdToken() : null;
    });

    if (!auth) {
      setLoading(false);
      return;
    }
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const token = await u.getIdTokenResult();
        setIsAdmin(token.claims.admin === true);
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return unsub;
  }, [auth]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      configured: firebaseConfigured,
      isAdmin,
      async signInEmail(email, password) {
        const a = getFirebaseAuth();
        if (!a) throw new Error('Authentication is not configured.');
        await signInWithEmailAndPassword(a, email, password);
      },
      async registerEmail(email, password, displayName) {
        const a = getFirebaseAuth();
        if (!a) throw new Error('Authentication is not configured.');
        const cred = await createUserWithEmailAndPassword(a, email, password);
        if (displayName) await updateProfile(cred.user, { displayName });
      },
      async signInGoogle() {
        const a = getFirebaseAuth();
        if (!a) throw new Error('Authentication is not configured.');
        await signInWithPopup(a, googleProvider);
      },
      async resetPassword(email) {
        const a = getFirebaseAuth();
        if (!a) throw new Error('Authentication is not configured.');
        await sendPasswordResetEmail(a, email);
      },
      async logout() {
        const a = getFirebaseAuth();
        if (a) await signOut(a);
      },
    }),
    [user, loading, isAdmin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
